// The customer-facing links for this store, in one place. QA and staff should
// never have to assemble a storefront URL by hand (V4-9 came in because there
// was no way to reach the customer portal from here), and a copy button
// matters as much as the link: the usual next step is pasting it into a phone.

import { useState } from 'react';
import { Check, Copy, ExternalLink, QrCode } from 'lucide-react';
import { storefrontUrl, tableUrl } from '../lib/links';

function LinkRow({ label, url, hint }: { label: string; url: string; hint?: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      // clipboard refused (insecure context, or the browser said no) — the
      // link is on screen and selectable, so this is not worth an error state
    }
  };
  return (
    <div className="link-row">
      <div className="link-main">
        <div className="link-label">{label}</div>
        <a className="link-url" href={url} target="_blank" rel="noreferrer" title={url}>
          {url}
        </a>
        {hint && <div className="link-hint">{hint}</div>}
      </div>
      <button className="btn btn-outline btn-sm" onClick={() => void copy()} aria-label={`Copy ${label} link`}>
        {copied ? <Check size={14} strokeWidth={2.4} /> : <Copy size={14} strokeWidth={2} />}
        {copied ? 'Copied' : 'Copy'}
      </button>
      <a className="btn btn-outline btn-sm" href={url} target="_blank" rel="noreferrer">
        <ExternalLink size={14} strokeWidth={2} />
        Open
      </a>
    </div>
  );
}

export function StoreLinks({
  rid,
  sid,
  tables,
}: {
  rid: string;
  sid: string;
  tables?: { tableId: string; name: string }[];
}) {
  const [showTables, setShowTables] = useState(false);
  return (
    <section className="panel store-links">
      <h2>Customer links</h2>
      <LinkRow label="Storefront" url={storefrontUrl(rid, sid)} hint="Pickup, delivery and dine-in ordering for this store." />
      {tables && tables.length > 0 && (
        <>
          <button className="btn btn-outline btn-sm reveal" onClick={() => setShowTables((v) => !v)}>
            <QrCode size={14} strokeWidth={2} />
            {showTables ? 'Hide table links' : `Table links (${tables.length})`}
          </button>
          {showTables &&
            tables.map((t) => (
              <LinkRow key={t.tableId} label={t.name} url={tableUrl(rid, sid, t.tableId)} />
            ))}
        </>
      )}
    </section>
  );
}
